import { Fragment, useCallback, useEffect, useState } from 'react';
import { upi, rupees, type DecisionReplay, type Payment } from '../../api/upi';
import UPIStatusBadge from './UPIStatusBadge';
import TestCheckout from './TestCheckout';
import { formatDate, shortDid } from './terms';

interface Props {
  role: 'customer' | 'agent';
  did: string;
  /** The mock bank is running: the customer can answer in its "bank app". */
  mockBank: boolean;
}

const SOURCES: Record<string, string> = { submit: 'when sent', bank: 'bank notified us', reconcile: 'we asked the bank' };

function Timeline({ p }: { p: Payment }) {
  const [replay, setReplay] = useState<DecisionReplay | null>(null);
  const [error, setError] = useState('');
  return (
    <div className="grid grid-cols-1 gap-4 bg-slate-50 px-5 py-4 text-xs sm:grid-cols-2">
      <div>
        <p className="mb-2 font-bold uppercase tracking-wider text-text-faint">History</p>
        <ol className="space-y-1.5">
          {(p.events ?? []).map((e, i) => (
            <li key={i} className="flex items-center gap-2">
              <UPIStatusBadge status={e.to} />
              <span className="text-text-muted">{formatDate(e.at)} · {SOURCES[e.source] ?? e.source}</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="space-y-1.5 text-text-muted">
        <p className="mb-2 font-bold uppercase tracking-wider text-text-faint">Details</p>
        <p>Order <span className="font-mono text-text-main">{p.merchantOrderId}</span> · merchant <span className="font-mono">{shortDid(p.merchantDid)}</span></p>
        <p>Agent <span className="font-mono">{shortDid(p.agentDid)}</span></p>
        <p>Bank <span className="font-mono">{shortDid(p.bankDid)}</span>{p.rrn ? <> · RRN <span className="font-mono text-text-main">{p.rrn}</span></> : null}</p>
        {p.reasonCode && <p>Reason <span className="font-mono text-text-main">{p.reasonCode}</span></p>}
        {p.decisionId && (
          <div className="pt-1">
            <button
              onClick={() => { setError(''); upi.replay(p.decisionId!).then(setReplay).catch((e) => setError(e instanceof Error ? e.message : String(e))); }}
              className="rounded-lg border border-border-subtle bg-white px-3 py-1 font-medium text-text-main hover:bg-slate-100"
            >
              Check the platform's decision
            </button>
            {replay && (
              <p className={`mt-1.5 ${replay.matches ? 'text-emerald-700' : 'text-red-700'}`}>
                {replay.matches
                  ? 'The signed decision record verifies and replays to the same result.'
                  : replay.signatureValid ? 'The record verifies but replays differently.' : 'The decision record does not verify.'}
              </p>
            )}
            {error && <p className="mt-1.5 text-red-700">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function PaymentsTab({ role, did, mockBank }: Props) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<Payment | null>(null);

  const load = useCallback(() => {
    upi.payments(role === 'customer' ? { customer: did } : { agent: did })
      .then((list) => { setPayments(list); setError(''); })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [role, did]);
  useEffect(() => { setLoading(true); load(); }, [load]);

  // Open payments move when the bank answers; look again every 10 seconds.
  const anyOpen = payments.some((p) => p.status === 'SUBMITTED' || p.status === 'PENDING_HUMAN');
  useEffect(() => {
    if (!anyOpen) return;
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [anyOpen, load]);

  const expand = (p: Payment) => {
    if (open?.id === p.id) return setOpen(null);
    upi.payment(p.id).then(setOpen).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  };

  const answer = async (p: Payment, approve: boolean) => {
    try {
      await upi.mockResolve(p.id, approve);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      {role === 'agent' && mockBank && <TestCheckout agentDid={did} onSubmitted={load} />}
      {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <p className="text-sm text-text-muted">Loading…</p>}
      {!loading && payments.length === 0 && (
        <div className="rounded-xl border border-dashed border-border-subtle p-8 text-center text-sm text-text-muted">No payments yet.</div>
      )}
      {payments.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-border-subtle bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle bg-slate-50">
              <tr>
                {['When', 'To', 'Amount', 'Status', ''].map((h) => (
                  <th key={h} className="px-5 py-2.5 text-left text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <Fragment key={p.id}>
                  <tr className="cursor-pointer border-b border-border-subtle hover:bg-slate-50" onClick={() => expand(p)}>
                    <td className="px-5 py-3 text-xs text-text-muted">{formatDate(p.createdAt)}</td>
                    <td className="px-5 py-3 font-mono text-xs">{p.payeeVpa}</td>
                    <td className="px-5 py-3 font-semibold">{rupees(p.amountPaise)}</td>
                    <td className="px-5 py-3"><UPIStatusBadge status={p.status} /></td>
                    <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {role === 'customer' && mockBank && p.status === 'PENDING_HUMAN' && (
                        <span className="inline-flex gap-2">
                          <button onClick={() => void answer(p, true)} className="btn-primary px-3 py-1 text-xs">Approve</button>
                          <button onClick={() => void answer(p, false)} className="rounded-lg border border-border-subtle px-3 py-1 text-xs hover:bg-slate-50">Decline</button>
                        </span>
                      )}
                    </td>
                  </tr>
                  {open?.id === p.id && (
                    <tr><td colSpan={5} className="p-0"><Timeline p={open} /></td></tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {role === 'customer' && mockBank && payments.some((p) => p.status === 'PENDING_HUMAN') && (
        <p className="text-[11px] text-text-faint">
          Approve and Decline stand in for your bank's app: this server runs the mock bank, which moves no money.
        </p>
      )}
    </div>
  );
}
